import { useEffect, useState } from "react";
import { FileSignature, Loader2, ExternalLink, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const ESTADO_BADGE = {
    borrador: "badge-gold", firmada: "badge-gold", enviada: "badge-exito",
    acuse_recibido: "badge-exito", en_ejecucion: "badge-gold", ejecutada: "badge-exito",
    rechazada: "badge-mock", cancelada: "badge-mock",
};

export default function Instrucciones() {
    const { selected } = useFideicomiso();
    const { hasRole, user } = useAuth();
    const [rows, setRows] = useState([]);
    const [partes, setPartes] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [open, setOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [folioIn, setFolioIn] = useState("");
    const [verifyResult, setVerifyResult] = useState(null);
    const [form, setForm] = useState({
        tipo: "pago", fecha_emision: "", emisor_parte_id: "",
        monto: "", moneda: "MXN",
        beneficiario: "", cuenta_origen_id: "", concepto: "",
    });
    const [busy, setBusy] = useState(false);

    const canWrite = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.OPERACIONES, ROLES.ADMIN_TI]);
    const canExecute = hasRole([ROLES.TESORERIA, ROLES.OPERACIONES, ROLES.ADMIN_TI]);

    const load = async () => {
        if (!selected) return;
        const [{ data: r }, { data: p }, { data: c }] = await Promise.all([
            api.get(`/instrucciones?fideicomiso_id=${selected.id}`),
            api.get(`/fideicomisos/${selected.id}/partes`),
            api.get(`/fideicomisos/${selected.id}/cuentas`),
        ]);
        setRows(r); setPartes(p); setCuentas(c);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/instrucciones?fideicomiso_id=${selected.id}`, {
                tipo: form.tipo, fecha_emision: form.fecha_emision,
                emisor_parte_id: form.emisor_parte_id,
                contenido: { beneficiario: form.beneficiario, cuenta_origen_id: form.cuenta_origen_id, concepto: form.concepto },
                monto: parseFloat(form.monto) || 0, moneda: form.moneda,
            });
            toast.success("Instrucción creada en borrador");
            setOpen(false); setForm({ tipo: "pago", fecha_emision: "", emisor_parte_id: "", monto: "", moneda: "MXN", beneficiario: "", cuenta_origen_id: "", concepto: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const action = async (id, path, body) => {
        try {
            const opts = body instanceof FormData ? { headers: { "Content-Type": "multipart/form-data" } } : undefined;
            const res = await api.post(`/instrucciones/${id}/${path}`, body ?? {}, opts);
            toast.success(`OK: ${path}`, { description: res.data.folio_verificable ? `Folio: ${res.data.folio_verificable}` : "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const firmar = async (id) => {
        const parteId = prompt("Parte firmante (ID). Ver ficha del fideicomiso para IDs de partes:");
        if (!parteId) return;
        const fd = new FormData();
        fd.append("parte_id", parteId);
        await action(id, "firmar", fd);
    };

    const verificar = async () => {
        try {
            const { data } = await api.get(`/verificar/${folioIn.toUpperCase().trim()}`);
            setVerifyResult(data);
        } catch (err) {
            setVerifyResult({ error: err?.response?.data?.detail || "Folio no encontrado" });
        }
    };

    if (!selected) {
        return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso en el header.</div>;
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="instrucciones-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <FileSignature className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Instrucciones
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Flujo: borrador → firmar → enviar (matriz facultades) → acuse → ejecutar.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline"><Search className="h-4 w-4 mr-2" /> Verificar folio</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Verificar folio público</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <Input placeholder="Folio de 16 caracteres" value={folioIn}
                                    onChange={(e) => setFolioIn(e.target.value)}
                                    data-testid="verificar-folio-input" />
                                <Button onClick={verificar} className="w-full" data-testid="verificar-folio-btn">
                                    <ShieldCheck className="h-4 w-4 mr-2" /> Verificar (endpoint público)
                                </Button>
                                {verifyResult && (
                                    <pre className="text-xs bg-muted p-3 rounded-sm overflow-x-auto max-h-64" data-testid="verify-result">
                                        {JSON.stringify(verifyResult, null, 2)}
                                    </pre>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                    {canWrite && (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="nueva-instruccion-btn">Nueva instrucción</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader><DialogTitle>Nueva instrucción</DialogTitle></DialogHeader>
                                <form onSubmit={submit} className="space-y-3" data-testid="nueva-instruccion-form">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label>Tipo</Label>
                                            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {["pago", "inversion", "liberacion", "sustitucion", "reversion", "modificacion_contrato", "otro"].map((t) => (
                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>Fecha emisión</Label>
                                            <Input type="date" required value={form.fecha_emision}
                                                onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })} />
                                        </div>
                                    </div>
                                    <div><Label>Emisor (parte)</Label>
                                        <Select value={form.emisor_parte_id} onValueChange={(v) => setForm({ ...form, emisor_parte_id: v })}>
                                            <SelectTrigger data-testid="emisor-select"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                                            <SelectContent>
                                                {partes.map((p) => <SelectItem key={p.id} value={p.id}>{p.persona?.nombre_display} · {p.rol}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label>Monto</Label><Input type="number" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} data-testid="monto-input" /></div>
                                        <div><Label>Moneda</Label>
                                            <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>{["MXN", "USD", "EUR"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div><Label>Beneficiario</Label><Input value={form.beneficiario} onChange={(e) => setForm({ ...form, beneficiario: e.target.value })} /></div>
                                    <div><Label>Cuenta origen</Label>
                                        <Select value={form.cuenta_origen_id} onValueChange={(v) => setForm({ ...form, cuenta_origen_id: v })}>
                                            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                            <SelectContent>
                                                {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.banco} {c.numero_cuenta} ({c.moneda})</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label>Concepto</Label><Textarea value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} /></div>
                                    <DialogFooter>
                                        <Button type="submit" disabled={busy} data-testid="submit-instruccion">
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear borrador"}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="instrucciones-table">
                    <thead><tr>
                        <th>Número</th><th>Tipo</th><th>Emisión</th><th>Monto</th>
                        <th>Firmas</th><th>Estado</th><th>Folio</th><th>Acciones</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((i) => (
                            <tr key={i.id} data-testid={`instruccion-row-${i.numero}`}>
                                <td className="font-medium">{i.numero}</td>
                                <td><span className="badge-gold">{i.tipo}</span></td>
                                <td className="tabular text-xs">{i.fecha_emision}</td>
                                <td className="tabular font-medium">
                                    {i.monto?.toLocaleString("es-MX", { style: "currency", currency: i.moneda || "MXN" })}
                                </td>
                                <td className="text-xs">{i.firmas?.length || 0}</td>
                                <td><span className={ESTADO_BADGE[i.estado] || "badge-gold"}>{i.estado}</span></td>
                                <td className="hash-cell">{i.folio_verificable || "—"}</td>
                                <td className="flex flex-wrap gap-1">
                                    {canWrite && (i.estado === "borrador" || i.estado === "firmada") && (
                                        <Button size="sm" variant="ghost" onClick={() => firmar(i.id)} data-testid={`firmar-${i.id}`}>Firmar</Button>
                                    )}
                                    {canWrite && i.estado === "firmada" && (
                                        <Button size="sm" variant="ghost" onClick={() => action(i.id, "enviar")} data-testid={`enviar-${i.id}`}>Enviar</Button>
                                    )}
                                    {canWrite && i.estado === "enviada" && (
                                        <Button size="sm" variant="ghost" onClick={() => action(i.id, "acuse")} data-testid={`acuse-${i.id}`}>Acuse</Button>
                                    )}
                                    {canExecute && (i.estado === "acuse_recibido" || i.estado === "enviada") && (
                                        <Button size="sm" variant="ghost" onClick={() => action(i.id, "ejecutar")} data-testid={`ejecutar-${i.id}`}>Ejecutar</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Sin instrucciones.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
